import React, { useState } from "react";
import { useTeamContext } from "../../contexts/TeamContext";
import { useJourneyTemplates } from "../../hooks/useJourneys";
import { Journey } from "../../types/journey";

// Props for the JourneyTemplatesList component
interface JourneyTemplatesListProps {
  onTemplateSelect: (template: Journey) => void;
}

/**
 * JourneyTemplatesList component
 *
 * Displays a list of journey templates fetched from the API that users can select
 * to use as a starting point for creating new journeys.
 */
const JourneyTemplatesList: React.FC<JourneyTemplatesListProps> = ({
  onTemplateSelect,
}) => {
  const { teamId } = useTeamContext();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );

  // Fetch journey templates from the API
  const {
    data: templates = [],
    isLoading,
    error,
  } = useJourneyTemplates(teamId);

  // Handle template selection
  const handleTemplateSelect = (template: Journey) => {
    setSelectedTemplateId(template.id);
    onTemplateSelect(template);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 px-6 bg-red-50 border border-red-200 rounded-md text-red-700">
        Error loading templates. Please try again.
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="py-4 px-6 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
        No templates available. Create a journey from scratch instead.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">Select a Template</h3>
      <p className="text-sm text-gray-500">
        Choose a template as a starting point for your journey or create from
        scratch.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
              selectedTemplateId === template.id
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/50"
            }`}
            onClick={() => handleTemplateSelect(template)}
          >
            <h4 className="text-base font-medium text-gray-900">
              {template.name}
            </h4>
            {template.description && (
              <p className="text-sm text-gray-600 mt-1">
                {template.description}
              </p>
            )}
            <div className="flex items-center mt-3 text-xs text-gray-500">
              <span className="mr-4">{template.step_count} steps</span>
              <span>Created by: {template.created_by_name}</span>
            </div>
          </div>
        ))}

        {/* Option to create from scratch */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
            selectedTemplateId === null
              ? "border-blue-500 bg-blue-50"
              : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/50"
          }`}
          onClick={() => {
            setSelectedTemplateId(null);
            onTemplateSelect(null as any); // Indicate no template selected
          }}
        >
          <h4 className="text-base font-medium text-gray-900">
            Create from Scratch
          </h4>
          <p className="text-sm text-gray-600 mt-1">
            Start with a blank journey and build it step by step.
          </p>
        </div>
      </div>
    </div>
  );
};

export default JourneyTemplatesList;
